import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  Pagination,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MouseIcon from "@mui/icons-material/Mouse";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useGetSmsUploadSummaryQuery,
  useGetNumbersByCampaignQuery,
  useGetSearchAcrossCampaignsQuery,
  useGetCustomerLogDeleteKeyQuery,
  useDeleteLogReportMutation,
  useGetDeliveryStatusCountQuery,
  useDownloadSmsReportByDateMutation,
  useDownloadClickerReportByDateMutation,
} from "../store/services/reportApi";
import {
  useGetcampaigndetailbyidQuery,
  useGetCampaignListByDateQuery,
  useGetRcsTemplateViewQuery,
  useLazyRcsdeliveryreportdownloadQuery,
  useLazyRcsdeliviryreportdownloadbycampaignidQuery,
  useLazyRcsdeliveryreportdownloadzipQuery,
  useGetrcsclickreportQuery,
} from "../store/services/rcstemplateApi";
import {
  useConstructionOutlinedQuery,
  useGetllmsepratetemplateQuery,
  useGetTemplatesQuery,
  useGetWhatsappCampaignListAdminQuery,
  useGetPhoneNUmberQuery,
} from "../store/services/whatsappApi";
import dayjs from "dayjs";
import ImageIcon from "@mui/icons-material/Image";
import utc from "dayjs/plugin/utc";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { cellSX, layoutCSS } from "../component/common/commonStyle";
import { toast } from "react-toastify";
import {
  useDownloadActualClicksReportByDateMutation,
  useDownloadClicksReportByDateMutation,
  useGetClickerStatusCountQuery,
  useGetClickerUploadSummaryQuery,
} from "../store/services/clickerApi";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useGetrcscampaiononlydataQuery } from "../store/services/rcstemplateApi";
import CampaignTable from "./rcsdeliveredreport";
dayjs.extend(utc);

const getCampaignButtonReplies = (row) => {
  if (!row?.messages?.length || !row?.replies?.length) return [];

  const outboundIds = row.messages
    .filter((m) => m.direction === "outbound" && m.message_id)
    .map((m) => m.message_id);

  if (outboundIds.length === 0) return [];

  return row.replies.filter(
    (r) =>
      r.message_data?.type === "button" &&
      outboundIds.includes(r.message_data?.context?.id),
  );
};

// Normalises whatever the LLM template API returns into an array of templates.
// Supports: { templates: [...] }, { data: [...] }, { data: {...} }, [...] or a single object.
const extractTemplateList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.templates)) return res.templates;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.templates)) return res.data.templates;
  if (res.data && typeof res.data === "object") return [res.data];
  if (res.template && typeof res.template === "object") return [res.template];
  if (res.components || res.name) return [res];
  return [];
};

// ─── IMAGE LOADER (put this OUTSIDE CustomerSmsReport) ───
const HeaderImage = ({ url, height = 120 }) => {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
  }, [url]);

  if (!url) {
    return (
      <Box
        sx={{
          width: "100%",
          height,
          bgcolor: "#ddd",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          mb: 1,
        }}
      >
        <ImageIcon color="disabled" />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height, mb: 1 }}>
      {status !== "loaded" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s infinite",
            "@keyframes shimmer": {
              "0%": { backgroundPosition: "200% 0" },
              "100%": { backgroundPosition: "-200% 0" },
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
          }}
        >
          {status === "error" && <ImageIcon color="disabled" />}
        </Box>
      )}
      <Box
        component="img"
        src={url}
        alt="header"
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        sx={{
          width: "100%",
          height,
          objectFit: "cover",
          display: "block",
          borderRadius: "4px",
          opacity: status === "loaded" ? 1 : 0,
          transition: "opacity 0.3s",
        }}
      />
    </Box>
  );
};

const CustomerSmsReport = () => {
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [reportType, setReportType] = useState("sms");
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [fileStatus, setFileStatus] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const navigate = useNavigate();
  const [searchPage, setSearchPage] = useState(1);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPage, setDialogPage] = useState(1);
  const [dialogStatus, setDialogStatus] = useState("");
  const [page, setPage] = useState(1);
  const [customerId, setCustomerId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [whatsappSearchVal, setWhatsappSearchVal] = useState("91");
  const [appliedWhatsappSearch, setAppliedWhatsappSearch] = useState("");
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);

  const [phoneNumbersDialogOpen, setPhoneNumbersDialogOpen] = useState(false);
  const [selectedCampaignForNumbers, setSelectedCampaignForNumbers] =
    useState(null);
  const [phoneNumbersData, setPhoneNumbersData] = useState([]);
  const [filteredPhoneNumbers, setFilteredPhoneNumbers] = useState([]);
  const [phoneNumberSearch, setPhoneNumberSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [triggerZipDownload, { isLoading: zipDownloadLoading }] =
    useLazyRcsdeliveryreportdownloadzipQuery();
  const [repliesDialogOpen, setRepliesDialogOpen] = useState(false);
  const [selectedCampaignForReplies, setSelectedCampaignForReplies] =
    useState(null);
  const [repliesData, setRepliesData] = useState([]);
  const [filteredReplies, setFilteredReplies] = useState([]);
  const [replySearch, setReplySearch] = useState("");
  const [replyTab, setReplyTab] = useState(0);
  const [searchTrigger, setSearchTrigger] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  // Click report popup state
  const [clickReportOpen, setClickReportOpen] = useState(false);
  const [clickReportMobile, setClickReportMobile] = useState(null);
  const [clickReportPage, setClickReportPage] = useState(1);
  const [clickReportRowsPerPage, setClickReportRowsPerPage] = useState(10);

  const [detailPage, setDetailPage] = useState(1);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);
  const [downloadSmsReportByDate, { isLoading: downloadLoading }] =
    useDownloadSmsReportByDateMutation();
  // Add these after your existing useState declarations
  const [rcsDetailsPage, setRcsDetailsPage] = useState(1);
  const [cachedRcsCampaign, setCachedRcsCampaign] = useState(null);
  const [cachedRcsOverview, setCachedRcsOverview] = useState(null);
  const [rcsMessageData, setRcsMessageData] = useState([]);
  const [rcsPagination, setRcsPagination] = useState(null);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);

  const { data: rcsCampaignOnlyData } = useGetrcscampaiononlydataQuery(
    {
      campaign_id: selectedCampaignId,
      page: rcsDetailsPage,
      pagesize: detailRowsPerPage,
    },
    {
      skip: !selectedCampaignId || !dialogOpen,
    },
  );
  console.log("rcsCampaignOnlyData", rcsCampaignOnlyData);

  const handleWhatsAppNumbersClick = (campaign) => {
    navigate(`/whatsappdetailpage/${campaign.campaign_id}`, {
      state: { campaign },
    });
  };
  const [
    triggerDownloadReport,
    {
      data: rcsdeliveryreportdownloadData,
      isLoading: rcsdeliveryreportdownloadLoading,
      isError: rcsdeliveryreportdownloadError,
    },
  ] = useLazyRcsdeliveryreportdownloadQuery();
  const { data } = useGetRcsTemplateViewQuery(selectedTemplateId);
  const [
    triggerDownloadByCampaignId,
    {
      data: rcsDeliveryByCampaignIdData,
      isLoading: rcsDeliveryByCampaignIdLoading,
    },
  ] = useLazyRcsdeliviryreportdownloadbycampaignidQuery();
  const [downloadClickerReportByDate, { isLoading: clickerDownloadLoading }] =
    useDownloadClickerReportByDateMutation();
  const [
    downloadClicksReportByDate,
    { isLoading: clicksReportDownloadLoading },
  ] = useDownloadClicksReportByDateMutation();
  const [
    downloadActualClicksReportByDate,
    { isLoading: actualClicksReportDownloadLoading },
  ] = useDownloadActualClicksReportByDateMutation();
  const inputSx = {
    borderRadius: "0.5rem",
    minWidth: 100,
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.7)",
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#94A3B8" },
    },
    "& .MuiInputLabel-root": { color: "#475569" },
    "& .Mui-disabled": {
      "& .MuiOutlinedInput-input": {
        color: "#334155 !important",
        WebkitTextFillColor: "#334155 !important",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
    },
  };
  const {
    data: rcsDeliveredData,
    isLoading: rcsDeliveredLoading,
    isError: rcsDeliveredError,
  } = useGetCampaignListByDateQuery(
    {
      startdate: startDate?.format("YYYY-MM-DD"),
      enddate: endDate?.format("YYYY-MM-DD"),
      page,
      limit: rowsPerPage,
    },
    {
      skip: reportType !== "rcs" || !shouldFetch,
      refetchOnMountOrArgChange: false,
    },
  );

  // ─── WHATSAPP PREVIEW STATE (stores the whole campaign row) ───
  const [whatsappPreviewOpen, setWhatsappPreviewOpen] = useState(false);
  const [previewCampaign, setPreviewCampaign] = useState(null);

  // 1) Normal template: by customer_id + template_id
  const { data: whatsappTemplates, isLoading: isNormalTemplateLoading } =
    useGetTemplatesQuery(
      { customer_id: customerId, template_id: previewCampaign?.template_id },
      {
        skip:
          !whatsappPreviewOpen || !customerId || !previewCampaign?.template_id,
      },
    );

  // 2) LLM template: customer_id + waba_id + base_template_name (sent as `template_name`)
  const { data: llmPreviewData, isLoading: isLlmTemplateLoading } =
    useGetllmsepratetemplateQuery(
      {
        customer_id: previewCampaign?.customer_id || customerId,
        waba_id: previewCampaign?.waba_id,
        template_name: previewCampaign?.base_template_name,
      },
      {
        skip:
          !whatsappPreviewOpen ||
          !(previewCampaign?.customer_id || customerId) ||
          !previewCampaign?.waba_id ||
          !previewCampaign?.base_template_name,
      },
    );

  // LLM response shape: { waba_id, templates: [{ original, pool, iterations }] }
  // The preview only needs the `original` template of the matching entry.
  const llmTemplate = (() => {
    const list = extractTemplateList(llmPreviewData);
    const entry =
      list.find(
        (t) => t?.original?.name === previewCampaign?.base_template_name,
      ) || list[0];
    return entry?.original || null;
  })();

  // Prefer the LLM variant, fall back to the normal template
  const previewTemplate = llmTemplate || whatsappTemplates?.templates?.[0] || null;
  const isWhatsappTemplatesLoading =
    isNormalTemplateLoading || isLlmTemplateLoading;

  const {
    data: rcsClickReport,
    isLoading: rcsClickReportLoading,
    isFetching: rcsClickReportFetching,
    error: rcsClickReportError,
  } = useGetrcsclickreportQuery(
    {
      campaign_id: selectedCampaignId,
      mobile_number: clickReportMobile,
      page: clickReportPage,
      limit: clickReportRowsPerPage,
    },
    { skip: !selectedCampaignId || !clickReportMobile },
  );
  console.log("rcsClickReport", rcsClickReport);

  const {
    data: campaignDetails,
    isLoading: loadingDetails,
    isFetching: loadingDetailsFetching,
  } = useGetcampaigndetailbyidQuery(
    {
      campaignId: selectedCampaignId,
      page: detailPage,
      pagesize: detailRowsPerPage,
    },
    {
      skip: !selectedCampaignId,
    },
  );

  const rcsList = rcsDeliveredData?.data || [];
  const summary = rcsList.reduce(
    (acc, item) => {
      const o = item.rcs_overview || {};

      acc.total += o.total || 0;
      acc.sent += o.sent || 0;
      acc.delivered += o.delivered || 0;
      acc.read += o.read || 0;
      acc.failed += o.failed || 0;
      acc.pending += o.pending || 0;
      acc.rejected += o.rejected || 0;
      acc.disabled += o.disabled || 0;
      acc.limitExhausted += o.limitExhausted || 0;

      return acc;
    },
    {
      total: 0,
      sent: 0,
      delivered: 0,
      read: 0,
      failed: 0,
      pending: 0,
      rejected: 0,
      disabled: 0,
      limitExhausted: 0,
    },
  );

  const handleNext = (cardCount) => {
    setSelectedCardIndex((prev) => (prev + 1) % cardCount);
  };

  const handlePrev = (cardCount) => {
    setSelectedCardIndex((prev) => (prev - 1 + cardCount) % cardCount);
  };
  const handleTemplateView = (id) => {
    setSelectedTemplateId(id);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedTemplateId(null);
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");

      if (!token) {
        toast.error("Access token not found in local storage");
        return;
      }

      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;

      if (!id) {
        toast.error("Customer ID not found in decoded token");
      } else {
        console.log("Setting customer_id from token:", id);
        setCustomerId(id);
      }
    } catch (err) {
      console.error("Error decoding token:", err);
      toast.error("Invalid access token format");
    }
  }, []);

  // ─── WHATSAPP QUERY (switched to Admin campaign list) ───
  const {
    data: whatsappReportData,
    isLoading: whatsappReportLoading,
    isError: whatsappReportError,
    refetch: refetchWhatsapp,
  } = useGetWhatsappCampaignListAdminQuery(
    {
      customer_id: customerId,
      page: page,
      limit: rowsPerPage,
      startdate: startDate?.format("YYYY-MM-DD"),
      enddate: endDate?.format("YYYY-MM-DD"),
    },
    {
      skip: reportType !== "whatsapp" || !customerId || !shouldFetch,
      refetchOnMountOrArgChange: false,
    },
  );

  const filteredWhatsappReport = (whatsappReportData?.data || []).filter(
    (campaign) => campaign?.status?.toLowerCase() !== "scheduled",
  );

  // ✅ support both snake_case and camelCase pagination keys from API
  const whatsappPagination = whatsappReportData?.pagination || {};
  const whatsappTotals = whatsappReportData?.totals || {};
  const whatsappTotalPages =
    whatsappPagination.totalPages ?? whatsappPagination.total_pages ?? 0;
  const whatsappTotalRecords =
    whatsappPagination.totalRecords ?? whatsappPagination.total ?? 0;

  // ─── PHONE NUMBERS (fetched together with the campaign list, same skip rule) ───
  const { data: phoneNumberData } = useGetPhoneNUmberQuery(
    customerId,
    {
      skip: reportType !== "whatsapp" || !customerId || !shouldFetch,
      refetchOnMountOrArgChange: false,
    },
  );

  // waba_id -> [phone numbers linked to that waba]
  const wabaPhoneMap = (phoneNumberData?.phone_numbers || []).reduce(
    (acc, item) => {
      if (!item?.waba_id || !item?.phone_number) return acc;
      if (!acc[item.waba_id]) acc[item.waba_id] = [];
      acc[item.waba_id].push(item.phone_number);
      return acc;
    },
    {},
  );

  const limit = rowsPerPage;
  const startdate = startDate?.format("YYYY-MM-DD");
  const enddate = endDate?.format("YYYY-MM-DD");

  const {
    data: smsSummaryData,
    refetch: refetchSmsSummary,
    isLoading: smsSummaryLoading,
    isError: smsSummaryError,
  } = useGetSmsUploadSummaryQuery(
    {
      startdate,
      enddate,
      file_status: fileStatus,
      page,
      limit,
    },
    {
      skip: reportType !== "sms" || !shouldFetch,
      refetchOnMountOrArgChange: false,
    },
  );

  const {
    data: deliveryStatusData,
    isLoading: deliveryStatusLoading,
    isError: deliveryStatusError,
  } = useGetDeliveryStatusCountQuery(
    { startdate, enddate },
    { skip: reportType !== "sms" },
  );
  console.log("deliveryStatusData", deliveryStatusData);

  // Clicker query
  const {
    data: clickerSummaryData,
    refetch: refetchClickerSummary,
    isLoading: clickerSummaryLoading,
    isError: clickerSummaryError,
  } = useGetClickerUploadSummaryQuery(
    {
      startdate,
      enddate,
      file_status: fileStatus,
      page,
      limit,
    },
    {
      skip: reportType !== "clicker" || !shouldFetch,
      refetchOnMountOrArgChange: false,
    },
  );

  let summaryData;
  let summaryLoading;
  let summaryError;
  let refetchSummary;

  if (reportType === "sms") {
    summaryData = smsSummaryData;
    summaryLoading = smsSummaryLoading;
    summaryError = smsSummaryError;
    refetchSummary = refetchSmsSummary;
  } else if (reportType === "clicker") {
    summaryData = clickerSummaryData;
    summaryLoading = clickerSummaryLoading;
    summaryError = clickerSummaryError;
    refetchSummary = refetchClickerSummary;
  } else if (reportType === "whatsapp") {
    summaryData = whatsappReportData;
    summaryLoading = whatsappReportLoading;
    summaryError = whatsappReportError;
    refetchSummary = refetchWhatsapp;
  }

  const handleDelete = async (campaign_id) => {
    if (window.confirm("Are you sure you want to delete this campaign log?")) {
      try {
        const res = await deleteLogReport({ campaign_id }).unwrap();
        toast.success(res?.message || "Log report deleted successfully.");
        refetchSummary();
      } catch (err) {
        console.error("Delete failed:", err);
        toast.error(err?.data?.message || "Something went wrong");
      }
    }
  };
  const { data: clickerCountData, isLoading: clickerCountLoading } =
    useGetClickerStatusCountQuery(
      {
        startdate,
        enddate,
        file_status: fileStatus,
      },
      {
        skip: reportType !== "clicker" || !shouldFetch,
        refetchOnMountOrArgChange: false,
      },
    );

  const handleDetailsClick = (campaignId) => {
    navigate(`/rcs-campaign-details/${campaignId}`);
  };
  const handleClickerDownloadReport = async () => {
    if (!startDate || !endDate) {
      alert("Please select start and end date");
      return;
    }

    try {
      const start_date = dayjs(startDate).format("YYYY-MM-DD");
      const end_date = dayjs(endDate).format("YYYY-MM-DD");

      const result = await downloadClickerReportByDate({
        start_date,
        end_date,
      }).unwrap();

      if (result instanceof Blob) {
        const downloadUrl = window.URL.createObjectURL(result);
        const link = document.createElement("a");

        link.href = downloadUrl;
        link.download = `clicker_report_${start_date}_to_${end_date}.csv`;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        return;
      }

      alert(result?.message || "No records found");
    } catch (error) {
      console.error("Clicker download report error:", error);
      alert(
        error?.data?.message ||
        error?.message ||
        "Failed to download clicker report",
      );
    }
  };

  const handleDownloadClickReport = async () => {
    if (!startDate || !endDate) {
      alert("Please select start and end date");
      return;
    }

    try {
      const start_date = dayjs(startDate).format("YYYY-MM-DD");
      const end_date = dayjs(endDate).format("YYYY-MM-DD");

      const result = await downloadClicksReportByDate({
        start_date,
        end_date,
        type: "all",
      }).unwrap();

      if (result instanceof Blob) {
        const downloadUrl = window.URL.createObjectURL(result);
        const link = document.createElement("a");

        link.href = downloadUrl;
        link.download = `clicks_report_${start_date}_to_${end_date}.csv`;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        return;
      }

      alert(result?.message || "No click records found");
    } catch (error) {
      console.error("Clicks report download error:", error);
      alert(
        error?.data?.message ||
        error?.message ||
        "Failed to download clicks report",
      );
    }
  };
  const handleDownloadActualClickReport = async () => {
    if (!startDate || !endDate) {
      alert("Please select start and end date");
      return;
    }

    try {
      const start_date = dayjs(startDate).format("YYYY-MM-DD");
      const end_date = dayjs(endDate).format("YYYY-MM-DD");

      const result = await downloadActualClicksReportByDate({
        start_date,
        end_date,
      }).unwrap();

      if (result instanceof Blob) {
        const downloadUrl = window.URL.createObjectURL(result);
        const link = document.createElement("a");

        link.href = downloadUrl;
        link.download = `actual_clicks_report_${start_date}_to_${end_date}.csv`;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        return;
      }

      alert(result?.message || "No actual click records found");
    } catch (error) {
      console.error("Actual clicks report download error:", error);
      alert(
        error?.data?.message ||
        error?.message ||
        "Failed to download actual clicks report",
      );
    }
  };
  const handleRcsDialogPageChange = (event, value) => {
    setRcsDetailsPage(value);
    setDetailPage(value);
  };
  const handleDownloadReport = async () => {
    if (!startDate || !endDate) {
      alert("Please select start and end date");
      return;
    }

    try {
      const start_date = dayjs(startDate).format("YYYY-MM-DD");
      const end_date = dayjs(endDate).format("YYYY-MM-DD");

      const result = await downloadSmsReportByDate({
        start_date,
        end_date,
      }).unwrap();

      if (result instanceof Blob) {
        const downloadUrl = window.URL.createObjectURL(result);
        const link = document.createElement("a");

        link.href = downloadUrl;
        link.download = `sms_report_${start_date}_to_${end_date}.csv`;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        return;
      }

      alert(result?.message || "No records found");
    } catch (error) {
      console.error("Download report error:", error);
      alert(
        error?.data?.message || error?.message || "Failed to download report",
      );
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCampaignId(null);
    setDetailPage(1);
    setDetailRowsPerPage(25);
    setCachedRcsCampaign(null);
    setCachedRcsOverview(null);
    setRcsMessageData([]);
    setRcsPagination(null);
    setRcsDetailsPage(1);
  };

  const csvSafe = (value) => {
    if (value === null || value === undefined) return "-";
    const str = String(value).replace(/"/g, '""');
    return `"${str}"`;
  };

  const downloadCsvFile = (csv, fileName) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}_${dayjs().format("YYYY-MM-DD_HHmmss")}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const generateDateRangeCsv = (rows, operatorName = "-") => {
    const headers = [
      "Number",
      "Provider",
      "Reason",
      "Submit Time",
      "Sent",
      "Delivered",
      "Read",
      "Retry",
      "TTL Expired",
      "Failed",
    ];

    const csvRows = [
      headers.join(","),
      ...rows.map((item) =>
        [
          csvSafe(item.number),
          csvSafe(operatorName),
          csvSafe(item.reason),
          csvSafe(item["Submit Time"]),
          csvSafe(item.SENT),
          csvSafe(item.DELIVERED),
          csvSafe(item.READ),
          csvSafe(item.RETRY),
          csvSafe(item.TTL_EXPIRATION_REVOKED),
          csvSafe(item.FAILED),
        ].join(","),
      ),
    ];

    downloadCsvFile(
      csvRows.join("\n"),
      `RCS_Delivery_Report_${startDate?.format("YYYY-MM-DD")}_${endDate?.format("YYYY-MM-DD")}`,
    );
  };

  const generateCampaignCsv = (rows) => {
    const header = [
      "Sr No",
      "Mobile Number",
      "Provider",
      "Submit Time",
      "Sent",
      "Delivered",
      "Read",
      "Retry",
      "TTL Expired",
      "Failed",
      "Reason",
    ];

    const csvRows = rows.map((row, index) => [
      index + 1,
      csvSafe(row.number),
      csvSafe(row.operatorName),
      csvSafe(row["Submit Time"]),
      csvSafe(row.SENT),
      csvSafe(row.DELIVERED),
      csvSafe(row.READ),
      csvSafe(row.RETRY),
      csvSafe(row.TTL_EXPIRATION_REVOKED),
      csvSafe(row.FAILED),
      csvSafe(row.reason),
    ]);

    const csvString = [
      header.join(","),
      ...csvRows.map((r) => r.join(",")),
    ].join("\n");

    downloadCsvFile(
      csvString,
      `RCS_Campaign_${selectedCampaignId}_Delivery_Report`,
    );
  };

  // Add this useEffect to cache data when API returns
  useEffect(() => {
    if (campaignDetails?.status && shouldFetchDetails && selectedCampaignId) {
      setCachedRcsCampaign(campaignDetails.campaign);
      setCachedRcsOverview(campaignDetails.overview);
      setRcsMessageData(campaignDetails.data || []);
      setRcsPagination(campaignDetails.pagination);
      setShouldFetchDetails(false);
    }
  }, [campaignDetails, shouldFetchDetails, selectedCampaignId]);

  const handleDownload = async () => {
    try {
      const loadingToast = toast.loading("Preparing ZIP download...");

      const result = await triggerZipDownload({
        startdate: startDate?.format("YYYY-MM-DD"),
        enddate: endDate?.format("YYYY-MM-DD"),
      }).unwrap();

      toast.dismiss(loadingToast);

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        result.fileName ||
        `rcs_report_${startDate?.format("YYYY-MM-DD")}_to_${endDate?.format("YYYY-MM-DD")}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success("Download started successfully!");
    } catch (err) {
      console.error("Download failed:", err);
      toast.error(err?.data?.message || "Failed to download report");
    }
  };

  const handleDownloadByCampaignId = async () => {
    if (!selectedCampaignId) {
      toast.error("No campaign selected");
      return;
    }

    try {
      const loadingToast = toast.loading("Preparing ZIP download...");

      const result = await triggerZipDownload({
        campaign_id: selectedCampaignId,
        startdate: startDate?.format("YYYY-MM-DD"),
        enddate: endDate?.format("YYYY-MM-DD"),
      }).unwrap();

      toast.dismiss(loadingToast);

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        result.fileName || `campaign_${selectedCampaignId}_report.zip`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success("Download started successfully!");
    } catch (err) {
      console.error("Campaign download failed:", err);
      toast.error(err?.data?.message || "Failed to download campaign report");
    }
  };

  const handleWhatsAppPhoneNumbersDownload = () => {
    if (!filteredPhoneNumbers || filteredPhoneNumbers.length === 0) {
      toast.error("No data available to download");
      return;
    }
    generateWhatsAppPhoneNumbersCsv(
      filteredPhoneNumbers,
      selectedCampaignForNumbers?.campaign_name || "Campaign",
    );
    toast.success("WhatsApp Phone Numbers download started");
  };

  const generateWhatsAppPhoneNumbersCsv = (data, campaignName) => {
    const headers = [
      "Phone Number",
      "Status",
      "Created Time",
      "Sent Time",
      "Delivered Time",
      "Read Time",
      "Failed Time",
      "Message ID",
      "Cost",
      "Delivery Time (ms)",
      "Read Time (ms)",
      "Total Time (ms)",
    ];

    const csvRows = [
      headers.join(","),
      ...data.map((item) =>
        [
          csvSafe(normalizePhoneNumber(item.phoneNumber)),
          csvSafe(item.status),
          csvSafe(formatTimestampString(item.createdTime)),
          csvSafe(formatTimestampString(item.sentTime)),
          csvSafe(formatTimestampString(item.deliveredTime)),
          csvSafe(formatTimestampString(item.readTime)),
          csvSafe(formatTimestampString(item.failedTime)),
          csvSafe(item.messageId),
          csvSafe(item.cost),
          csvSafe(item.deliveryTimeMs),
          csvSafe(item.readTimeMs),
          csvSafe(item.totalTimeMs),
        ].join(","),
      ),
    ];

    downloadCsvFile(
      csvRows.join("\n"),
      `WhatsApp_Phone_Numbers_${campaignName}`,
    );
  };

  // Receives the whole campaign row so we have template_id, base_template_name, waba_id, customer_id
  const handleOpenWhatsappPreview = (campaign) => {
    setPreviewCampaign(campaign);
    setWhatsappPreviewOpen(true);
  };

  const handleCloseWhatsappPreview = () => {
    setWhatsappPreviewOpen(false);
    setPreviewCampaign(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = dayjs(dateString);
    if (!d.isValid()) return "N/A";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <Typography
          variant="body2"
          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
        >
          {d.format("DD/MM/YYYY")}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontSize: "0.7rem", color: "text.secondary" }}
        >
          {d.format("HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const formatTimestamp = (dateString) => {
    return formatDate(dateString);
  };

  const formatTimestampString = (dateString) => {
    if (!dateString) return "N/A";
    const d = dayjs(dateString);
    return d.isValid() ? d.format("DD/MM/YYYY,HH:mm:ss") : "N/A";
  };

  const formatWhatsAppDate = (dateString) => {
    if (!dateString) return "-";
    const d = dayjs(dateString);
    return d.isValid() ? d.format("DD/MM/YYYY,HH:mm:ss") : "-";
  };

  const formattime = (dateString) => {
    return null;
  };

  useEffect(() => {
    setSearchPage(1);
  }, [globalSearch, startDate, endDate]);

  // Auto-trigger WhatsApp search when input reaches 12 characters
  useEffect(() => {
    if (reportType === "whatsapp" && whatsappSearchVal.length === 12) {
      setAppliedWhatsappSearch(whatsappSearchVal);
    }
  }, [whatsappSearchVal, reportType]);

  const {
    data: deleteKeyData,
    isLoading: deleteKeyLoading,
    isError: deleteKeyError,
  } = useGetCustomerLogDeleteKeyQuery();
  const allowDelete = deleteKeyData?.data?.allow_delete_logreport === true;
  const allowLiveReport = deleteKeyData?.data?.allow_live_report === true;
  const showReportCount = deleteKeyData?.data?.show_report_count === true;

  const [deleteLogReport, { isLoading: deleting }] =
    useDeleteLogReportMutation();

  const {
    data: numbersData,
    isLoading: numbersLoading,
    isError: numbersError,
  } = useGetNumbersByCampaignQuery(
    {
      campaign_id: selectedCampaign?.campaign_id,
      page: dialogPage,
      stat: dialogStatus,
    },
    { skip: !selectedCampaign || !dialogOpen },
  );

  useEffect(() => {
    setPage(1);
  }, [startdate, enddate, fileStatus, reportType]);

  const { data: searchData, isLoading: searchLoading } =
    useGetSearchAcrossCampaignsQuery(
      {
        ph_no: reportType === "whatsapp" ? appliedWhatsappSearch : globalSearch,
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
        page: searchPage,
      },
      {
        skip:
          (reportType === "whatsapp"
            ? !appliedWhatsappSearch
            : !globalSearch) ||
          !startDate ||
          !endDate,
      },
    );

  const filteredSearchData = React.useMemo(() => {
    if (!globalSearch && !appliedWhatsappSearch) return [];
    if (reportType === "sms" || reportType === "rcs") {
      return searchData?.data || [];
    }

    if (reportType === "whatsapp") {
      const searchTerm = appliedWhatsappSearch || globalSearch;
      return (
        filteredWhatsappReport?.filter(
          (campaign) =>
            campaign.campaign_id
              ?.toString()
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            campaign.campaign_name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()),
        ) || []
      );
    }

    if (reportType === "clicker") {
      return (
        clickerSummaryData?.data?.filter(
          (campaign) =>
            campaign.clicker_campaign_id
              ?.toString()
              .includes(globalSearch.toLowerCase()) ||
            campaign.smstext
              ?.toLowerCase()
              .includes(globalSearch.toLowerCase()) ||
            campaign.header_name
              ?.toLowerCase()
              .includes(globalSearch.toLowerCase()),
        ) || []
      );
    }

    return [];
  }, [
    reportType,
    globalSearch,
    appliedWhatsappSearch,
    searchData,
    filteredWhatsappReport,
    clickerSummaryData,
  ]);

  useEffect(() => {
    if (reportType === "clicker" && clickerCountData?.summary) {
      const summary = clickerCountData.summary;
    }
  }, [reportType, clickerCountData]);
  const handleSearchPageChange = (_, value) => {
    setSearchPage(value);
  };

  const handleOpenDialog = (campaign, status) => {
    if (reportType === "sms") {
      navigate(
        `/numbersbycampaign/${campaign.campaign_id}/${status || "All"}`,
        {
          state: { post_via: campaign.post_via, reportType: "sms" },
        },
      );
    } else if (reportType === "clicker") {
      navigate(
        `/numbersbycampaign/${campaign.clicker_campaign_id}/${status || "All"}`,
        {
          state: { post_via: campaign.post_via, reportType: "clicker" },
        },
      );
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedCampaign(null);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
    setShouldFetch(true);
    setSearchTrigger(true);
  };
  const handleReportTypeChange = (e) => {
    setReportType(e.target.value);
    setShouldFetch(false);
    setSearchTrigger(false);
    setPage(1);
  };

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    setShouldFetch(false);
    setSearchTrigger(false);
    if (endDate && newValue && endDate.diff(newValue, "month", true) > 1) {
      setEndDate(newValue.add(1, "month"));
    }
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
    setShouldFetch(false);
    setSearchTrigger(false);
  };
  const getTemplateMessageText = (messageData) => {
    if (!messageData) return "No message content";

    if (messageData.type === "template" && messageData.template) {
      const template = messageData.template;
      let messageText = `Template: ${template.name}`;

      if (template.components && Array.isArray(template.components)) {
        const bodyComponent = template.components.find(
          (comp) => comp.type === "BODY",
        );
        if (bodyComponent && bodyComponent.text) {
          messageText = bodyComponent.text;
        }

        const buttonsComponent = template.components.find(
          (comp) => comp.type === "BUTTONS",
        );
        if (buttonsComponent && buttonsComponent.buttons) {
          const buttonTexts = buttonsComponent.buttons
            .map((btn) => btn.text)
            .join(", ");
          if (buttonTexts) {
            messageText += ` [Buttons: ${buttonTexts}]`;
          }
        }
      }

      return messageText;
    }

    switch (messageData.type) {
      case "text":
        return messageData.text?.body || "Text message";
      case "button":
        return messageData.button?.text
          ? `Button: ${messageData.button.text}`
          : "Button response";
      case "interactive":
        return (
          messageData.interactive?.button_reply?.title ||
          messageData.interactive?.list_reply?.title ||
          "Interactive response"
        );
      case "unsupported":
        return "Unsupported message type";
      default:
        return messageData.type ? `${messageData.type} message` : "Message";
    }
  };

  const handleOpenRepliesDialog = (campaign) => {
    setSelectedCampaignForReplies(campaign);

    const inboundReplies = [];

    const campaignReplies = getCampaignButtonReplies(campaign);

    if (campaignReplies?.length) {
      campaignReplies.forEach((reply) => {
        inboundReplies.push({
          id: reply.id,
          message_text:
            reply.message_data?.button?.text ||
            getTemplateMessageText(reply.message_data),
          message_type: reply.message_data?.type,
          direction: "inbound",
          phone_number: reply.message_data?.from,
          waba_number: reply.waba_id,
          status: reply.status,
          created_at: reply.created_at,
          rawData: reply,
        });
      });
    }

    inboundReplies.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );

    setRepliesData(inboundReplies);
    setFilteredReplies(inboundReplies);
    setReplySearch("");
    setRepliesDialogOpen(true);
  };

  const handleCloseRepliesDialog = () => {
    setRepliesDialogOpen(false);
    setSelectedCampaignForReplies(null);
    setRepliesData([]);
    setFilteredReplies([]);
    setReplySearch("");
    setReplyTab(0);
  };

  useEffect(() => {
    if (searchTrigger && shouldFetch) {
      if (reportType === "rcs") {
        // RCS auto-fetches when skip condition changes, no refetch needed
      } else if (reportType === "whatsapp") {
        refetchWhatsapp();
      } else if (reportType === "clicker") {
        refetchClickerSummary();
      } else if (reportType === "sms") {
        refetchSmsSummary();
      }
      setSearchTrigger(false);
    }
  }, [searchTrigger, shouldFetch, reportType]);

  useEffect(() => {
    let filtered = repliesData;

    if (replyTab === 1) {
      filtered = filtered.filter((msg) => msg.direction === "inbound");
    } else if (replyTab === 2) {
      filtered = filtered.filter((msg) => msg.direction === "outbound");
    }

    if (replySearch) {
      const searchLower = replySearch.toLowerCase();
      filtered = filtered.filter(
        (msg) =>
          msg.message_text?.toLowerCase().includes(searchLower) ||
          msg.phone_number?.includes(searchLower) ||
          msg.message_type?.toLowerCase().includes(searchLower),
      );
    }

    setFilteredReplies(filtered);
  }, [replySearch, replyTab, repliesData]);

  const handleOpenPhoneNumbersDialog = (campaign) => {
    setSelectedCampaignForNumbers(campaign);

    const detailedPhoneNumbers =
      campaign?.messages
        ?.map((message) => ({
          phoneNumber: normalizePhoneNumber(message.message_data?.to),
          status: message.status,
          createdTime: message.created_at,
          sentTime: message.sent_at,
          deliveredTime: message.delivered_at,
          readTime: message.read_at,
          failedTime: message.failed_at,
          messageId: message.message_id,
          cost: message.cost,
          deliveryTimeMs: message.delivery_time_ms,
          readTimeMs: message.read_time_ms,
          totalTimeMs: message.total_time_ms,
          statusCode: message.status_code,
          errorCategory: message.error_category,
          errorDescription: message.description,
        }))
        ?.filter((item) => item.phoneNumber) || [];

    setPhoneNumbersData(detailedPhoneNumbers);
    setFilteredPhoneNumbers(detailedPhoneNumbers);
    setPhoneNumberSearch("");
    setStatusFilter("all");
    setPhoneNumbersDialogOpen(true);
  };

  const handleClosePhoneNumbersDialog = () => {
    setPhoneNumbersDialogOpen(false);
    setSelectedCampaignForNumbers(null);
    setPhoneNumbersData([]);
    setFilteredPhoneNumbers([]);
    setPhoneNumberSearch("");
    setStatusFilter("all");
  };

  useEffect(() => {
    let filtered = phoneNumbersData;

    if (phoneNumberSearch) {
      filtered = filtered.filter((item) =>
        item.phoneNumber
          ?.toLowerCase()
          .includes(phoneNumberSearch.toLowerCase()),
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    setFilteredPhoneNumbers(filtered);
  }, [phoneNumberSearch, statusFilter, phoneNumbersData]);

  useEffect(() => {
    if (campaignDetails?.status && selectedCampaignId && dialogOpen) {
      if (
        cachedRcsCampaign &&
        cachedRcsCampaign.campaign_id === selectedCampaignId
      ) {
        setRcsMessageData(campaignDetails.data || []);
        setRcsPagination(campaignDetails.pagination);
      } else {
        setCachedRcsCampaign(campaignDetails.campaign);
        setCachedRcsOverview(campaignDetails.overview);
        setRcsMessageData(campaignDetails.data || []);
        setRcsPagination(campaignDetails.pagination);
      }
    }
  }, [campaignDetails, selectedCampaignId, dialogOpen, rcsDetailsPage]);

  const handleClearFilters = () => {
    setPhoneNumberSearch("");
    setStatusFilter("all");
  };
  const handleSearch = () => {
    setShouldFetch(true);
    setSearchTrigger(true);
    setPage(1);
  };

  const handleFileStatusChange = (e) => {
    setFileStatus(e.target.value);
    setShouldFetch(false);
    setSearchTrigger(false);
    setPage(1);
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "success";
      case "read":
        return "primary";
      case "sent":
        return "info";
      case "failed":
        return "error";
      case "EXECUTED":
        return "success";
      default:
        return "default";
    }
  };

  const normalizePhoneNumber = (phone) => {
    if (phone === null || phone === undefined) return "";
    if (typeof phone === "string" || typeof phone === "number") {
      return String(phone);
    }
    if (Array.isArray(phone)) {
      return phone.map(normalizePhoneNumber).filter(Boolean).join(", ");
    }
    if (typeof phone === "object") {
      return (
        phone.phone ||
        phone.number ||
        phone.id ||
        phone.msisdn ||
        phone.to ||
        phone.from ||
        JSON.stringify(phone)
      );
    }
    return String(phone);
  };

  const maskPhone = (num) => {
    const str = normalizePhoneNumber(num);
    if (!str) return "";
    return str.slice(0, 3) + "****" + str.slice(-3);
  };

  const formatTimeDuration = (ms) => {
    if (!ms) return "-";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}
      >
        Delivery Report
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          elevation: 2,
          borderRadius: "0.5rem",
          padding: "1rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartDateChange}
            maxDate={dayjs()}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            maxDate={
              startDate
                ? startDate.add(1, "month").isBefore(dayjs())
                  ? startDate.add(1, "month")
                  : dayjs()
                : dayjs()
            }
            minDate={startDate}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel
            id="report-type-label"
            sx={{
              fontSize: "0.75rem",
              color: "#475569",
              "&.Mui-focused": { color: "#000000" },
            }}
          >
            Report Type
          </InputLabel>
          <Select
            labelId="report-type-label"
            value={reportType}
            label="Report Type"
            onChange={(e) => setReportType(e.target.value)}
            sx={{
              height: 35,
              fontSize: "0.75rem",
              borderRadius: "8px",
              background: "rgba(255, 255, 255, 0.8)",
              transition: "all 0.2s ease",
              "& .MuiSelect-select": {
                py: 0.5,
                fontSize: "0.75rem",
              },
              "&:hover": {
                boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
              },
              "&.Mui-focused": {
                boxShadow: "0 0 0 2px rgba(14,165,233,0.2)",
              },
            }}
          >
            <MenuItem value="sms" sx={{ fontSize: "0.75rem" }}>
              SMS
            </MenuItem>
            <MenuItem value="clicker" sx={{ fontSize: "0.75rem" }}>
              Clicker
            </MenuItem>
            <MenuItem value="rcs" sx={{ fontSize: "0.75rem" }}>
              RCS
            </MenuItem>
            <MenuItem value="whatsapp" sx={{ fontSize: "0.75rem" }}>
              WhatsApp
            </MenuItem>
          </Select>
        </FormControl>
        {reportType === "whatsapp" ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              label="Search Phone Number"
              value={whatsappSearchVal}
              onChange={(e) => {
                const val = e.target.value;
                if (
                  val.startsWith("91") &&
                  val.length <= 12 &&
                  /^\d*$/.test(val)
                ) {
                  setWhatsappSearchVal(val);
                } else if (val === "" || val === "9") {
                  setWhatsappSearchVal("91");
                }
              }}
              size="small"
              sx={{
                width: 150,
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                  padding: "6px 8px",
                  color: "#1E293B",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "1rem",
                  color: "#475569",
                  "&.Mui-focused": { color: "#000000" },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#000000", borderWidth: "1px" },
                  "&:hover fieldset": { borderColor: "#000000" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                    borderWidth: "2px",
                  },
                },
              }}
            />
          </Box>
        ) : (
          <TextField
            label="Search Phone Number"
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            size="small"
            sx={{
              width: 130,
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "6px 8px",
                color: "#1E293B",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.75rem",
                color: "#475569",
                "&.Mui-focused": { color: "#000000" },
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#000000", borderWidth: "1px" },
                "&:hover fieldset": { borderColor: "#000000" },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                  borderWidth: "2px",
                },
              },
            }}
          />
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          sx={{ fontSize: "0.75rem", minWidth: "80px" }}
        >
          <SearchIcon fontSize="small" />
        </Button>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
      >
        <Box>
          {reportType === "sms" && showReportCount && (
            <>
              {deliveryStatusLoading ? (
                <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  Loading...
                </Typography>
              ) : deliveryStatusData?.data ? (
                <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  {deliveryStatusData.data}
                </Typography>
              ) : null}
            </>
          )}

          {reportType === "whatsapp" && (
            <>
              {whatsappReportLoading ? (
                <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  Loading WhatsApp summary...
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                  TOTAL: {Number(whatsappTotals.submitted) || 0}{" "}
                  | DELIVERED:{" "}
                  {(Number(whatsappTotals.delivered) || 0) + (Number(whatsappTotals.read) || 0)}{" "}
                  | READ:{" "}
                  {Number(whatsappTotals.read) || 0}{" "}
                  | FAILED:{" "}
                  {Number(whatsappTotals.failed) || 0}{" "}
                  | PENDING: {Number(whatsappTotals.pending) || 0}
                </Typography>
              )}
            </>
          )}
        </Box>

        <FormControl size="small" sx={{ minWidth: 10 }}>
          <InputLabel
            id="rows-per-page-label"
            sx={{ fontSize: "0.75rem" }}
          ></InputLabel>
          <Select
            labelId="rows-per-page-label"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
              setShouldFetch(false);
              setSearchTrigger(false);
            }}
            sx={{ fontSize: "0.75rem" }}
          >
            {[25, 50, 100, 500].map((num) => (
              <MenuItem key={num} value={num} sx={{ fontSize: "0.75rem" }}>
                {num}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {reportType === "rcs" && (
        <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
          TOTAL: {summary.total} | SENT: {summary.sent} | DELIVRD:{" "}
          {summary.delivered} | FAILED: {summary.failed} | READ: {summary.read}{" "}
          | PENDING: {summary.pending} | REJECTED: {summary.rejected} |
          DISABLED: {summary.disabled} | LIMIT EXHAUSTED:{" "}
          {summary.limitExhausted}
        </Typography>
      )}
      {reportType === "clicker" && (
        <>
          {clickerCountLoading ? (
            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              Loading...
            </Typography>
          ) : clickerCountData?.data ? (
            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
              {clickerCountData.data}
            </Typography>
          ) : null}
        </>
      )}
      {(reportType === "whatsapp" ? appliedWhatsappSearch : globalSearch) && (
        <>
          {(!startDate || !endDate) && (
            <Box mt={2} textAlign="center">
              <Typography color="text.secondary">
                Please select start date and end date
              </Typography>
            </Box>
          )}

          {startDate && endDate && (
            <>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead
                    sx={{
                      bgcolor: "#0288d1",
                      "& th": {
                        color: "white",
                        fontSize: "0.8rem",
                        padding: "6px",
                        borderBottom: "0.5px solid #1b1b1bff",
                        borderRight: "0.5px solid #1b1b1bff",
                      },
                    }}
                  >
                    {reportType === "sms" && (
                      <TableRow>
                        <TableCell>Created At</TableCell>
                        <TableCell>Campaign ID</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>SMS Text</TableCell>
                        <TableCell>Sender ID</TableCell>
                        <TableCell>Error Code</TableCell>
                        <TableCell>fid</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    )}
                    {reportType === "clicker" && (
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Campaign ID</TableCell>
                        <TableCell>Total Numbers</TableCell>
                        <TableCell>Total Count</TableCell>
                        <TableCell>SMS Text</TableCell>
                        <TableCell>SenderID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Campaign category</TableCell>
                        {allowDelete && <TableCell>Action</TableCell>}
                      </TableRow>
                    )}
                    {reportType === "rcs" && (
                      <TableRow>
                        <TableCell>Created At</TableCell>
                        <TableCell>Campaign ID</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>SMS Text</TableCell>
                        <TableCell>Error Code</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    )}
                    {reportType === "whatsapp" && (
                      <TableRow>
                        <TableCell>Campaign ID</TableCell>
                        <TableCell>Campaign Name</TableCell>
                        <TableCell>Template Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Sent At</TableCell>
                        <TableCell>Failed</TableCell>
                      </TableRow>
                    )}
                  </TableHead>

                  <TableBody>
                    {reportType === "sms" && searchLoading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    )}
                    {reportType === "rcs" && searchLoading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    )}

                    {!searchLoading && filteredSearchData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={15} align="center">
                          No matching record found
                        </TableCell>
                      </TableRow>
                    )}

                    {reportType === "sms" &&
                      filteredSearchData.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={cellSX}>{formatDate(item.createdat)}</TableCell>
                          <TableCell sx={cellSX}>{item.campaign_id}</TableCell>
                          <TableCell sx={cellSX}>{item.ph_no}</TableCell>
                          <TableCell sx={cellSX}>
                            {item.totalLogs?.smstext || item.smstext || "N/A"}
                          </TableCell>
                          <TableCell sx={cellSX}>{item.sender_id}</TableCell>
                          <TableCell sx={cellSX}>{item.errcode}</TableCell>
                          <TableCell sx={cellSX}>{item.fid}</TableCell>
                          <TableCell sx={cellSX}>{item.stat}</TableCell>
                        </TableRow>
                      ))}

                    {reportType === "rcs" &&
                      filteredSearchData.map((item, index) => (
                        <TableRow key={`${item.campaign_id}-${index}`}>
                          <TableCell sx={cellSX}>{formatDate(item.createdat)}</TableCell>
                          <TableCell sx={cellSX}>{item.campaign_id || "-"}</TableCell>
                          <TableCell sx={cellSX}>{item.ph_no || "-"}</TableCell>
                          <TableCell sx={cellSX}>
                            {item.totalLogs?.smstext || item.smstext || "N/A"}
                          </TableCell>
                          <TableCell sx={cellSX}>{item.errcode || "-"}</TableCell>
                          <TableCell sx={cellSX}>{item.stat || "-"}</TableCell>
                        </TableRow>
                      ))}

                    {reportType === "clicker" &&
                      filteredSearchData.map((campaign) => (
                        <TableRow key={campaign.campaign_id}>
                          <TableCell sx={cellSX}>{formatDate(campaign.time_to_send)}</TableCell>
                          <TableCell sx={cellSX}>{campaign.clicker_campaign_id}</TableCell>
                          <TableCell sx={cellSX}>{campaign.total_numbers}</TableCell>
                          <TableCell sx={cellSX}>{campaign.total_count}</TableCell>
                          <TableCell sx={cellSX}>{campaign.smstext}</TableCell>
                          <TableCell sx={cellSX}>{campaign.header_name}</TableCell>
                          <TableCell sx={cellSX}>{campaign.file_status}</TableCell>
                          <TableCell sx={cellSX}>{campaign.post_via}</TableCell>
                          <TableCell sx={cellSX}>{campaign.file_source}</TableCell>
                        </TableRow>
                      ))}

                    {/* WhatsApp search results – new response shape */}
                    {reportType === "whatsapp" &&
                      filteredSearchData.map((campaign) => (
                        <TableRow key={campaign.campaign_id}>
                          <TableCell sx={cellSX}>{campaign.campaign_id || "-"}</TableCell>
                          <TableCell sx={cellSX}>{campaign.campaign_name || "-"}</TableCell>
                          <TableCell sx={cellSX}>{campaign.template_name || "-"}</TableCell>
                          <TableCell sx={cellSX}>{campaign.status || "-"}</TableCell>
                          <TableCell sx={cellSX}>
                            {formatTimestamp(campaign.created_at)}
                          </TableCell>
                          <TableCell sx={cellSX}>
                            {formatTimestamp(campaign.sent_at)}
                          </TableCell>
                          <TableCell sx={cellSX}>
                            {campaign.messages?.failed || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {searchData?.data?.length > 0 &&
                searchData?.pagination?.totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={searchData.pagination.totalPages}
                      page={searchPage}
                      onChange={handleSearchPageChange}
                      color="primary"
                    />
                  </Box>
                )}
            </>
          )}
        </>
      )}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": {
                color: "white",
                fontSize: "0.8rem",
                padding: "6px",
                borderBottom: "0.5px solid #1b1b1bff",
                borderRight: "0.5px solid #1b1b1bff",
              },
            }}
          >
            {reportType === "sms" && (
              <TableRow>
                <TableCell sx={{ width: "80px" }}>Date</TableCell>
                <TableCell sx={{ width: "80px" }}>Campaign ID</TableCell>
                <TableCell sx={{ width: "60px" }}>Total Numbers</TableCell>
                <TableCell sx={{ width: "60px" }}>Msg Count</TableCell>
                <TableCell sx={{ width: "60px" }}>Total Count</TableCell>
                <TableCell>Template Name </TableCell>
                <TableCell sx={{ width: "80px" }}>SenderID</TableCell>
                <TableCell sx={{ width: "80px" }}>Status</TableCell>
                <TableCell sx={{ width: "80px" }}>Campaign category</TableCell>
                <TableCell sx={{ width: "80px" }}>File Source</TableCell>
                {allowDelete && (
                  <TableCell sx={{ width: "30px", borderRight: "none" }}>
                    Action
                  </TableCell>
                )}
              </TableRow>
            )}

            {reportType === "rcs" && (
              <TableRow>
                <TableCell className="p-3 text-left">Sr No</TableCell>
                <TableCell className="p-3 text-left">Campaign Name</TableCell>
                <TableCell className="p-3 text-left">Bot Name</TableCell>
                <TableCell className="p-3 text-left">Template Name</TableCell>
                <TableCell className="p-3 text-left">Created At</TableCell>
                <TableCell className="p-3 text-left">Executed At</TableCell>
                <TableCell className="p-3 text-left">
                  Campaign category
                </TableCell>
                <TableCell className="p-3 text-left">Status</TableCell>
                <TableCell className="p-3 text-left">RCS Overview</TableCell>
                <TableCell
                  className="p-3 text-left"
                  sx={{ borderBottom: "#1b1b1bff" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            )}

            {reportType === "clicker" && (
              <TableRow>
                <TableCell sx={{ width: "80px" }}>Date</TableCell>
                <TableCell sx={{ width: "80px" }}>Campaign ID</TableCell>
                <TableCell sx={{ width: "60px" }}>Total Numbers</TableCell>
                <TableCell sx={{ width: "60px" }}>Total Count</TableCell>
                <TableCell>Template Name</TableCell>
                <TableCell sx={{ width: "100px" }}>SenderID</TableCell>
                <TableCell sx={{ width: "100px" }}>Status</TableCell>
                <TableCell sx={{ width: "100px" }}>Campaign category</TableCell>
                <TableCell sx={{ width: "100px" }}>File Source</TableCell>
                {allowDelete && (
                  <TableCell sx={{ width: "30px", borderRight: "none" }}>
                    Action
                  </TableCell>
                )}
              </TableRow>
            )}

            {reportType === "whatsapp" && (
              <TableRow>
                <TableCell sx={{ minWidth: "100px", textAlign: "center" }}>
                  Campaign ID
                </TableCell>
                <TableCell sx={{ minWidth: "120px", textAlign: "center" }}>
                  Campaign Name
                </TableCell>
                <TableCell sx={{ minWidth: "120px", textAlign: "center" }}>
                  Template Name
                </TableCell>
                <TableCell sx={{ minWidth: "100px", textAlign: "center" }}>
                  Template Category
                </TableCell>
                <TableCell sx={{ minWidth: "120px", textAlign: "center" }}>
                  Waba Number
                </TableCell>
                <TableCell sx={{ minWidth: "100px", textAlign: "center" }}>
                  Total Numbers
                </TableCell>
                <TableCell sx={{ minWidth: "120px", textAlign: "center" }}>
                  Created date
                </TableCell>
                <TableCell sx={{ minWidth: "120px", textAlign: "center" }}>
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    minWidth: "80px",
                    textAlign: "center",
                    borderRight: "none",
                  }}
                >
                  Preview
                </TableCell>
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {reportType === "rcs" &&
              rcsDeliveredData?.data?.map((row) => (
                <TableRow key={row.campaign_id}>
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "80px" }}
                  >
                    <Typography variant="body2" fontSize="0.75rem">
                      {row.campaign_id}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "8%" }}
                  >
                    <Typography variant="body2" fontSize="0.8rem" noWrap>
                      {row.campaign_name}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "10%" }}
                  >
                    <Typography variant="body2" fontSize="0.8rem" noWrap>
                      {row.bot_name}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "6%" }}
                  >
                    <Typography variant="body2" fontSize="0.8rem" noWrap>
                      {row.template_name}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "8%" }}
                  >
                    <Typography variant="body2" fontSize="0.8rem" noWrap>
                      {formatDate(row.created_at)}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "8%" }}
                  >
                    <Typography variant="body2" fontSize="0.8rem" noWrap>
                      {formatDate(row.executed_at)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "8%" }}
                  >
                    <Typography variant="body2" fontSize="0.8rem" noWrap>
                      {row.campaign_type}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      border: "1px solid #1b1b1bff",
                      color:
                        row.status === "EXECUTED"
                          ? "success.main"
                          : "warning.main",
                    }}
                  >
                    <Typography variant="body2" fontSize="0.8rem">
                      {row.status}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      border: "1px solid #1b1b1bff",
                      minWidth: "120px",
                      maxWidth: "120px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          sx={{
                            color: "info.main",
                            fontSize: "0.85rem",
                            fontWeight: 500,
                          }}
                        >
                          ✓
                        </Typography>
                        <Typography
                          variant="caption"
                          fontSize="0.75rem"
                          sx={{ color: "info.main" }}
                        >
                          Sent: {row.rcs_overview.sent}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Typography
                          sx={{
                            color: "success.main",
                            fontSize: "0.85rem",
                            fontWeight: 500,
                          }}
                        >
                          ✓✓
                        </Typography>
                        <Typography
                          variant="caption"
                          fontSize="0.75rem"
                          sx={{ color: "success.main" }}
                        >
                          Delivered: {row.rcs_overview.delivered}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <DoneAllIcon
                          fontSize="small"
                          sx={{
                            fontSize: "0.85rem",
                            color: "secondary.main",
                          }}
                        />
                        <Typography
                          variant="caption"
                          fontSize="0.75rem"
                          sx={{ color: "secondary.main" }}
                        >
                          Read: {row.rcs_overview.read}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <MouseIcon
                          sx={{
                            fontSize: "0.85rem",
                            color: "warning.main",
                          }}
                        />
                        <Typography
                          variant="caption"
                          fontSize="0.75rem"
                          sx={{ color: "warning.main" }}
                        >
                          Clicks: {row.rcs_overview.click}
                        </Typography>
                      </Box>

                      {row.rcs_overview.failed > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              color: "error.main",
                              fontSize: "0.85rem",
                            }}
                          >
                            ⚠️
                          </Typography>
                          <Typography
                            variant="caption"
                            fontSize="0.75rem"
                            sx={{ color: "error.main" }}
                          >
                            Failed: {row.rcs_overview.failed}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ borderBottom: "0.5px solid #1b1b1bff" }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDetailsClick(row.campaign_id)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0)",
                          transform: "scale(1.1)",
                          transition: "all 0.2s ease",
                        },
                        borderBottom: "0.5px solid #1b1b1bff",
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="black"
                      size="small"
                      onClick={() => handleTemplateView(row.template_id)}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0)",
                          transform: "scale(1.1)",
                          transition: "all 0.2s ease",
                        },
                        borderBottom: "0.5px solid #1b1b1bff",
                      }}
                    >
                      <PhoneAndroidIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

            {/* ─── WHATSAPP MAIN TABLE (new response shape) ─── */}
            {reportType === "whatsapp" &&
              filteredWhatsappReport?.map((campaign) => {
                const sentCount = Number(campaign?.messages?.sent) || 0;
                const deliveredCount = Number(campaign?.messages?.delivered) || 0;
                const readCount = Number(campaign?.messages?.read) || 0;

                return (
                <TableRow key={campaign?.campaign_id}>
                  {/* Campaign ID */}
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "60px" }}
                  >
                    {campaign?.campaign_id || "-"}
                  </TableCell>

                  {/* Campaign Name */}
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "8%" }}
                  >
                    <Typography variant="body2" noWrap>
                      {campaign?.campaign_name || "-"}
                    </Typography>
                  </TableCell>

                  {/* Template Name */}
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "10%" }}
                  >
                    <Typography variant="body2" noWrap>
                      {campaign?.template_name || "-"}
                    </Typography>
                  </TableCell>

                  {/* Campaign Type */}
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "10%" }}
                  >
                    <Typography variant="body2" noWrap>
                      {campaign?.original_template_category || "-"}
                    </Typography>
                  </TableCell>

                  {/* Phone Number(s) linked to this campaign's waba_id */}
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "10%" }}
                  >
                    {wabaPhoneMap[campaign?.waba_id]?.length > 0 ? (
                      wabaPhoneMap[campaign.waba_id].map((num) => (
                        <Typography key={num} variant="body2" noWrap>
                          {num}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>

                  {/* Total Numbers – messages.total */}
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "10%" }}
                  >
                    {campaign?.messages?.total > 0 ? (
                      <Box
                        component="span"
                        onClick={() => handleWhatsAppNumbersClick(campaign)}
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontWeight: "bold",
                          fontSize: "14px",
                          "&:hover": { color: "primary.dark" },
                        }}
                      >
                        {campaign.messages.total}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {campaign?.messages?.total || 0}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Created At */}
                  <TableCell
                    sx={{ border: "1px solid #1b1b1bff", width: "12%" }}
                  >
                    <Typography variant="body2" noWrap>
                      {formatWhatsAppDate(campaign?.created_at)}
                    </Typography>
                  </TableCell>

                  {/* Status summary – aggregated */}
                  <TableCell
                    sx={{
                      border: "1px solid #1b1b1bff",
                      minWidth: "120px",
                      maxWidth: "120px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        {campaign?.file_status || "-"}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          ✓
                        </Typography>
                        <Typography variant="caption">
                          Sent: {sentCount + deliveredCount + readCount}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          ✓✓
                        </Typography>
                        <Typography variant="caption">
                          Delivered: {deliveredCount + readCount}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <DoneAllIcon
                          fontSize="small"
                          sx={{ color: "primary.main" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "primary.main" }}
                        >
                          Read: {readCount}
                        </Typography>
                      </Box>

                      {campaign?.messages?.failed > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "error.main" }}
                          >
                            ⚠️
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "error.main" }}
                          >
                            Failed: {campaign?.messages?.failed || 0}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  {/* Phone preview – passes the whole campaign row */}
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #1b1b1bff",
                      borderLeft: "1px solid #1b1b1bff",
                      width: "8%",
                    }}
                    align="center"
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleOpenWhatsappPreview(campaign)}
                      sx={{
                        ml: 1,
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "rgba(25, 118, 210, 0.04)",
                        },
                      }}
                    >
                      <PhoneAndroidIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}

            {/* Generic Renderer for SMS and Clicker */}
            {(reportType === "sms" || reportType === "clicker") && (
              <>
                {summaryLoading ? (
                  <TableRow>
                    <TableCell
                      sx={cellSX}
                      colSpan={reportType === "clicker" ? 11 : 9}
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : summaryError && startDate && endDate ? (
                  <TableRow>
                    <TableCell
                      sx={cellSX}
                      colSpan={reportType === "clicker" ? 11 : 9}
                    >
                      No record found
                    </TableCell>
                  </TableRow>
                ) : summaryData?.data?.length > 0 ? (
                  summaryData.data.map((campaign) => {
                    const totalcount =
                      (campaign.total_numbers || 0) * (campaign.msgcount || 1);
                    return (
                      <TableRow key={campaign.campaign_id}>
                        <TableCell sx={cellSX}>
                          {/* {formatDate(campaign.time_to_send)} */}
                          {formatDate(campaign.time_to_send, campaign.post_via === "customize")}
                        </TableCell>
                        <TableCell sx={cellSX} align="center">
                          {reportType === "clicker"
                            ? campaign.clicker_campaign_id
                            : campaign.campaign_id}
                        </TableCell>
                        <TableCell sx={cellSX} align="center">
                          <Typography
                            color="primary"
                            sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                            onClick={() => handleOpenDialog(campaign, "")}
                          >
                            {campaign.total_numbers}
                          </Typography>
                        </TableCell>
                        {reportType === "sms" && (
                          <TableCell sx={cellSX} align="center">
                            {campaign.msgcount}
                          </TableCell>
                        )}
                        <TableCell sx={cellSX} align="center">
                          {campaign.total_count}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {campaign.template_name || "-"}

                          {(reportType === "sms" ||
                            reportType === "clicker") && (
                            <Tooltip title="View SMS Text">
                              <VisibilityIcon
                                sx={{
                                  fontSize: 16,
                                  ml: 1,
                                  cursor: "pointer",
                                  color: "#1976d2",
                                }}
                                onClick={() => {
                                  setSelectedText(campaign.smstext);
                                  setOpenDialog(true);
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>{" "}
                        <TableCell sx={cellSX}>
                          {campaign.header_name}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {campaign.file_status}
                        </TableCell>
                        <TableCell sx={cellSX}>{campaign.post_via}</TableCell>
                        <TableCell sx={cellSX}>{campaign.file_source}</TableCell>
                        {allowDelete && (
                          <TableCell sx={{ ...cellSX, borderRight: "none" }}>
                            <IconButton
                              color="error"
                              onClick={
                                deleting
                                  ? undefined
                                  : () => handleDelete(campaign.campaign_id)
                              }
                              disabled={deleting}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={reportType === "clicker" ? 9 : 9}>
                      No campaigns found
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}

            {/* WhatsApp Loading/Error States */}
            {reportType === "whatsapp" && whatsappReportLoading && (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: "center", py: 3 }}>
                  Loading...
                </TableCell>
              </TableRow>
            )}
            {reportType === "whatsapp" &&
              !whatsappReportLoading &&
              (!filteredWhatsappReport ||
                filteredWhatsappReport.length === 0) && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 3 }}>
                    No campaigns found
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add pagination controls for main table */}
      {startDate && endDate && (
        <>
          {reportType === "rcs" &&
            (rcsDeliveredData?.pagination?.totalPages > 1 ||
              rcsDeliveredData?.pagination?.total_pages > 1) && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={
                    rcsDeliveredData.pagination.totalPages ||
                    rcsDeliveredData.pagination.total_pages
                  }
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}

          {reportType === "whatsapp" && whatsappTotalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={whatsappTotalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}

          {(reportType === "sms" || reportType === "clicker") &&
            (summaryData?.pagination?.totalPages > 1 ||
              summaryData?.pagination?.total_pages > 1) && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={
                    summaryData.pagination.totalPages ||
                    summaryData.pagination.total_pages
                  }
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}

          {reportType === "rcs" &&
            (rcsDeliveredData?.pagination?.totalRecords !== undefined ||
              rcsDeliveredData?.pagination?.total !== undefined) && (
              <Box display="flex" justifyContent="flex-end" mt={1} pr={2}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Total Records:{" "}
                  {rcsDeliveredData.pagination.totalRecords ||
                    rcsDeliveredData.pagination.total}
                </Typography>
              </Box>
            )}

          {reportType === "whatsapp" && whatsappTotalRecords > 0 && (
            <Box display="flex" justifyContent="flex-end" mt={1} pr={2}>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.75rem", fontWeight: 500 }}
              >
                Total Records: {whatsappTotalRecords}
              </Typography>
            </Box>
          )}

          {(reportType === "sms" || reportType === "clicker") &&
            (summaryData?.pagination?.totalRecords !== undefined ||
              summaryData?.pagination?.total !== undefined) && (
              <Box display="flex" justifyContent="flex-end" mt={1} pr={2}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Total Records:{" "}
                  {summaryData.pagination.totalRecords ||
                    summaryData.pagination.total}
                </Typography>
              </Box>
            )}
        </>
      )}

      {/* Replies Popup Dialog */}
      <Dialog
        open={repliesDialogOpen}
        onClose={handleCloseRepliesDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#0288d1",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
              <ChatBubbleIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              WhatsApp Replies - {selectedCampaignForReplies?.campaign_name}
            </Typography>
            <Typography variant="body2" sx={{ color: "white", mt: 0.5 }}>
              Campaign ID: {selectedCampaignForReplies?.campaign_id} | Total
              Replies: {repliesData.length}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseRepliesDialog}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{ mb: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Customer Replies ({repliesData.length})
              </Typography>

              <TextField
                placeholder="Search replies..."
                value={replySearch}
                onChange={(e) => setReplySearch(e.target.value)}
                size="small"
                sx={{ width: 250 }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{
                        color: "text.secondary",
                        mr: 1,
                        fontSize: "0.9rem",
                      }}
                    />
                  ),
                  endAdornment: replySearch && (
                    <IconButton size="small" onClick={() => setReplySearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={`Total Replies: ${repliesData.length}`}
                color="success"
                variant="outlined"
                size="small"
              />
              {repliesData.length > 0 && (
                <>
                  <Chip
                    label={`Text Messages: ${repliesData.filter((msg) => msg.message_type === "text").length}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`Interactive: ${repliesData.filter((msg) => msg.message_type === "interactive").length}`}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`Button: ${repliesData.filter((msg) => msg.message_type === "button").length}`}
                    color="warning"
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`Template: ${repliesData.filter((msg) => msg.message_type === "template").length}`}
                    color="info"
                    variant="outlined"
                    size="small"
                  />
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ maxHeight: "50vh", overflow: "auto" }}>
            {filteredReplies.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" stickyHeader>
                  <TableHead
                    sx={{
                      bgcolor: "#f5f5f5",
                      "& th": {
                        fontSize: "0.8rem",
                        padding: "8px",
                        fontWeight: "bold",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell sx={{ width: "50px" }}>#</TableCell>
                      <TableCell sx={{ minWidth: "120px" }}>
                        Phone Number
                      </TableCell>
                      <TableCell sx={{ minWidth: "250px" }}>Message</TableCell>
                      <TableCell sx={{ width: "120px" }}>
                        Message Type
                      </TableCell>
                      <TableCell sx={{ width: "150px" }}>
                        Received Time
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReplies.map((msg, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: "#f0f9ff",
                          "&:hover": { backgroundColor: "#e3f2fd" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.8rem" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8rem" }}>
                          {maskPhone(msg.phone_number)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8rem" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={msg.message_text}
                          >
                            {msg.message_text}
                          </Typography>
                          {msg.rawData?.template_parameters && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                mt: 0.5,
                                fontSize: "0.7rem",
                              }}
                            >
                              Template Params:{" "}
                              {JSON.stringify(msg.rawData.template_parameters)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={msg.message_type?.toUpperCase() || "UNKNOWN"}
                            color="primary"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8rem" }}>
                          {formatTimestamp(msg.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 4 }}
              >
                {repliesData.length === 0
                  ? "No replies found for this campaign."
                  : "No replies match your search criteria."}
              </Typography>
            )}
          </Box>

          {repliesData.length > 0 && (
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> Showing only customer replies. Campaign
                messages are not included in this view.
              </Typography>
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              onClick={handleCloseRepliesDialog}
              variant="contained"
              size="small"
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Phone Numbers Popup Dialog */}
      <Dialog
        open={phoneNumbersDialogOpen}
        onClose={handleClosePhoneNumbersDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxHeight: "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#0288d1",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
              Phone Numbers - {selectedCampaignForNumbers?.campaign_name}
            </Typography>
            <Typography variant="body2" sx={{ color: "white", mt: 0.5 }}>
              Campaign ID: {selectedCampaignForNumbers?.campaign_id} | Total
              Numbers: {phoneNumbersData.length}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleWhatsAppPhoneNumbersDownload}
              sx={{
                mr: 1,
                bgcolor: "white",
                color: "#ffffffff",
                "&:hover": { bgcolor: "#1b1b1bff" },
              }}
            >
              Download CSV
            </Button>
            <IconButton
              onClick={handleClosePhoneNumbersDialog}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{ mb: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Filter Phone Numbers
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="Search Phone Number"
                value={phoneNumberSearch}
                onChange={(e) => setPhoneNumberSearch(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                }}
                placeholder="Enter phone number..."
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>

              {(phoneNumberSearch || statusFilter !== "all") && (
                <Button
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  size="small"
                  variant="outlined"
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Box>

          {phoneNumbersData.length > 0 && (
            <Box
              sx={{ mb: 2, p: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Status Summary:
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {(() => {
                  const total = phoneNumbersData.length;
                  const sentCount = phoneNumbersData.filter(
                    (item) => item.status === "sent",
                  ).length;
                  const deliveredCount = phoneNumbersData.filter(
                    (item) => item.status === "delivered",
                  ).length;
                  const readCount = phoneNumbersData.filter(
                    (item) => item.status === "read",
                  ).length;
                  const failedCount = phoneNumbersData.filter(
                    (item) => item.status === "failed",
                  ).length;

                  return (
                    <>
                      <Chip
                        label={`Total: ${total}`}
                        color="default"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`Sent: ${total}`}
                        color="info"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`Delivered: ${deliveredCount}`}
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`Read: ${readCount}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`Failed: ${failedCount}`}
                        color="error"
                        variant="outlined"
                        size="small"
                      />
                    </>
                  );
                })()}
              </Box>
            </Box>
          )}

          <Box sx={{ maxHeight: "50vh", overflow: "auto" }}>
            {filteredPhoneNumbers.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small" stickyHeader>
                  <TableHead
                    sx={{
                      bgcolor: "#0288d1",
                      "& th": {
                        color: "white",
                        fontSize: "0.8rem",
                        padding: "6px",
                        borderBottom: "0.5px solid #1b1b1bff",
                        borderRight: "0.5px solid #1b1b1bff",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "50px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "130px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Phone Number
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "100px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "150px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Created Time
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "100px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Sent Time
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "100px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Delivered Time
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "100px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Read Time
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "100px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Failed Time
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "80px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Delivery Time
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          minWidth: "200px",
                          bgcolor: "#0288d1",
                        }}
                      >
                        Error Msg
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPhoneNumbers.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={cellSX}>{index + 1}</TableCell>
                        <TableCell sx={cellSX}>
                          {maskPhone(item.phoneNumber)}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          <Chip
                            label={item.status?.toUpperCase() || "UNKNOWN"}
                            color={getStatusColor(item.status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {formatTimestamp(item.createdTime)}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {formatTimestamp(item.sentTime)}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {formatTimestamp(item.deliveredTime)}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {formatTimestamp(item.readTime)}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {formatTimestamp(item.failedTime)}
                        </TableCell>
                        <TableCell sx={cellSX}>
                          {formatTimeDuration(item.deliveryTimeMs)}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellSX,
                            maxWidth: "220px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            fontSize: "0.75rem",
                            fontWeight: item.status === "failed" ? 600 : 400,
                            color:
                              item.status === "failed" ? "#c62828" : "#555",
                            backgroundColor:
                              item.status === "failed"
                                ? "rgba(198, 40, 40, 0.06)"
                                : "transparent",
                          }}
                        >
                          {item.errorDescription ? (
                            <span
                              title={`Code: ${item.statusCode || "-"} | Category: ${item.errorCategory || "-"}`}
                            >
                              {item.errorDescription}
                            </span>
                          ) : item.errorCategory ? (
                            <span title={`Code: ${item.statusCode || "-"}`}>
                              {item.errorCategory.replace(/_/g, " ")}
                            </span>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                {phoneNumbersData.length === 0
                  ? "No phone numbers found for this campaign."
                  : "No phone numbers match your filters."}
              </Typography>
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              onClick={handleClosePhoneNumbersDialog}
              variant="contained"
              size="small"
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        key={clickReportMobile}
        open={clickReportOpen}
        onClose={() => {
          setClickReportOpen(false);
          setClickReportMobile(null);
          setClickReportPage(1);
          setClickReportRowsPerPage(10);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#0288d1",
            color: "white",
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Click Report - {maskPhone(clickReportMobile)}
          </Typography>
          <IconButton
            onClick={() => {
              setClickReportOpen(false);
              setClickReportMobile(null);
              setClickReportPage(1);
              setClickReportRowsPerPage(10);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {rcsClickReportLoading || rcsClickReportFetching ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : rcsClickReportError ? (
            <Typography
              color="white"
              style={{ fontWeight: "bold" }}
              textAlign="center"
              py={4}
            >
              {" "}
              {rcsClickReportError?.data?.message || "Unknown error"}
            </Typography>
          ) : rcsClickReport?.status && rcsClickReport.data?.length > 0 ? (
            <>
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.32)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  Campaign Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" fontWeight={500}>
                      Campaign:
                    </Typography>
                    <Typography variant="body2">
                      {rcsClickReport.campaign_info.campaign_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" fontWeight={500}>
                      Template:
                    </Typography>
                    <Typography variant="body2">
                      {rcsClickReport.campaign_info.template_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" fontWeight={500}>
                      Agent:
                    </Typography>
                    <Typography variant="body2">
                      {rcsClickReport.campaign_info.agent_name}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  Click Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" fontWeight={500}>
                      Total Clicks
                    </Typography>
                    <Typography variant="h6">
                      {rcsClickReport.summary.total_clicks}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" fontWeight={500}>
                      Unique Buttons
                    </Typography>
                    <Typography variant="h6">
                      {rcsClickReport.summary.unique_buttons}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={500}>
                      Last Click
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(rcsClickReport.summary.last_click_time)}
                    </Typography>
                  </Grid>
                </Grid>

                {rcsClickReport.summary.button_breakdown?.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Button Breakdown
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Button Text</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Clicks</TableCell>
                            <TableCell>Last Click</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rcsClickReport.summary.button_breakdown.map(
                            (btn, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{btn.button_text}</TableCell>
                                <TableCell>{btn.button_type}</TableCell>
                                <TableCell align="center">
                                  {btn.total_clicks}
                                </TableCell>
                                <TableCell>
                                  {formatDate(btn.last_click_time)}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Paper>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Click Details
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Total: {rcsClickReport.pagination.totalRecords} records
                </Typography>
              </Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableHead sx={{ background: "#B8CCE4" }}>
                    <TableRow>
                      <TableCell>Sr No</TableCell>
                      <TableCell>Button Text</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Click Time</TableCell>
                      <TableCell>Provider</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rcsClickReport.data.map((click) => (
                      <TableRow key={click.sr_no}>
                        <TableCell>{click.sr_no}</TableCell>
                        <TableCell>{click.button_text}</TableCell>
                        <TableCell>{click.button_type}</TableCell>
                        <TableCell>{formatDate(click.click_time)}</TableCell>
                        <TableCell>{click.provider}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {rcsClickReport.pagination.totalPages > 1 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                >
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={clickReportRowsPerPage}
                      onChange={(e) => {
                        setClickReportRowsPerPage(Number(e.target.value));
                        setClickReportPage(1);
                      }}
                      sx={{ fontSize: "0.8rem", height: 32 }}
                    >
                      {[10, 25, 50].map((num) => (
                        <MenuItem key={num} value={num}>
                          {num} Rows
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={rcsClickReport.pagination.totalPages}
                    page={clickReportPage}
                    onChange={(_, value) => setClickReportPage(value)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography textAlign="center" py={4}>
              No click data available for this number.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={viewOpen}
        onClose={handleViewClose}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            p: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,0.5)",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            pt: 6,
            backgroundColor: "transparent",
          }}
        >
          <Box
            sx={{
              width: "300px",
              minHeight: "400px",
              maxHeight: "75vh",
              backgroundColor: "#fff",
              border: "8px solid #000",
              borderRadius: "32px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 15px 50px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "30px",
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#000",
                color: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "0.75rem" }}>
                {currentTime}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <WifiIcon sx={{ fontSize: "16px" }} />
                <NetworkCellIcon sx={{ fontSize: "16px" }} />
                <BatteryFullIcon sx={{ fontSize: "16px" }} />
              </Stack>
            </Box>

            <Button
              onClick={handleViewClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                minWidth: 0,
                padding: 0.5,
                zIndex: 10,
                color: "#000",
              }}
            >
              <ClearIcon fontSize="small" />
            </Button>

            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                flexGrow: 1,
                backgroundColor: "#f5f5f5",
                minHeight: "300px",
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  bgcolor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#ccc",
                  borderRadius: "2px",
                  "&:hover": {
                    bgcolor: "#999",
                  },
                },
              }}
            >
              {data?.data ? (
                <Stack spacing={2}>
                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      p: 2,
                      borderBottom: "1px solid #ccc",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: "0.95rem",
                        color: "#000",
                      }}
                    >
                      {data.data.templateDetails?.template_name}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        backgroundColor: "#f0f0f0",
                        px: 1,
                        py: 0.3,
                        borderRadius: "4px",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {data.data.templateDetails?.rcs_template_type}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      p: 2,
                      borderBottom: "1px solid #ccc",
                    }}
                  >
                    {(() => {
                      const type = data.data.templateDetails?.rcs_template_type;

                      switch (type) {
                        case "Text":
                          return (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#333",
                                lineHeight: 1.6,
                                fontSize: "0.9rem",
                              }}
                            >
                              {data.data.templateDetails?.rcs_text}
                            </Typography>
                          );

                        case "Rich Card":
                          return (
                            <Stack spacing={1.5}>
                              <Box
                                sx={{
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  backgroundColor: "#f0f0f0",
                                }}
                              >
                                <img
                                  src={
                                    data.data.templateDetails?.rich_card_image
                                  }
                                  alt="Rich Card"
                                  style={{
                                    width: "100%",
                                    borderRadius: "8px",
                                    display: "block",
                                  }}
                                />
                              </Box>

                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 700,
                                    mb: 0.5,
                                    color: "#000",
                                  }}
                                >
                                  {data.data.templateDetails?.rich_card_title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#666", lineHeight: 1.5 }}
                                >
                                  {
                                    data.data.templateDetails
                                      ?.rich_card_description
                                  }
                                </Typography>
                              </Box>
                            </Stack>
                          );

                        case "Carousel":
                          const cards = data.data.carousel_cards || [];
                          const selectedCard = cards[selectedCardIndex] || null;

                          return (
                            <Stack spacing={1.5}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  justifyContent: "center",
                                }}
                              >
                                {cards.map((_, idx) => (
                                  <Button
                                    key={idx}
                                    onClick={() => setSelectedCardIndex(idx)}
                                    sx={{
                                      minWidth: "32px",
                                      height: "32px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      transition: "all 0.3s ease",
                                      ...(selectedCardIndex === idx
                                        ? {
                                          backgroundColor: "#000",
                                          color: "#fff",
                                        }
                                        : {
                                          backgroundColor: "#1b1b1bff",
                                          color: "#333",
                                          "&:hover": {
                                            backgroundColor: "#ccc",
                                          },
                                        }),
                                    }}
                                  >
                                    {idx + 1}
                                  </Button>
                                ))}
                              </Box>

                              {selectedCard ? (
                                <Stack spacing={1}>
                                  <Box
                                    sx={{
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <img
                                      src={selectedCard.carousel_image}
                                      alt={`Card ${selectedCard.card_no}`}
                                      style={{
                                        width: "100%",
                                        borderRadius: "8px",
                                        display: "block",
                                      }}
                                    />
                                  </Box>

                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 700,
                                        mb: 0.5,
                                        color: "#000",
                                      }}
                                    >
                                      {selectedCard.carousel_title}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#666", lineHeight: 1.5 }}
                                    >
                                      {selectedCard.carousel_description}
                                    </Typography>
                                  </Box>
                                </Stack>
                              ) : (
                                <Typography color="text.secondary">
                                  No card data available.
                                </Typography>
                              )}
                            </Stack>
                          );

                        default:
                          return (
                            <Typography color="text.secondary">
                              Unknown type
                            </Typography>
                          );
                      }
                    })()}
                  </Box>

                  {data.data.buttonInformation?.length > 0 && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        p: 2,
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      <Stack spacing={1.5}>
                        {data.data.buttonInformation.filter(
                          (btn) => btn.button_action_id === 1,
                        ).length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: "#666",
                                  textTransform: "uppercase",
                                  fontSize: "0.65rem",
                                }}
                              >
                                Suggested Replies
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                              >
                                {data.data.buttonInformation
                                  .filter((btn) => btn.button_action_id === 1)
                                  .map((btn, idx) => (
                                    <Button
                                      key={`action1-${idx}`}
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        borderRadius: "6px",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        border: "1px solid #ccc",
                                        color: "#333",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                          backgroundColor: "#f0f0f0",
                                          borderColor: "#999",
                                        },
                                      }}
                                    >
                                      {btn.suggestion_text}
                                    </Button>
                                  ))}
                              </Stack>
                            </Box>
                          )}

                        {data.data.buttonInformation.filter(
                          (btn) => btn.button_action_id !== 1,
                        ).length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: "#666",
                                  textTransform: "uppercase",
                                  fontSize: "0.65rem",
                                }}
                              >
                                Suggested Actions
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                              >
                                {data.data.buttonInformation
                                  .filter((btn) => btn.button_action_id !== 1)
                                  .map((btn, idx) => (
                                    <Button
                                      key={`other-${idx}`}
                                      variant="contained"
                                      size="small"
                                      sx={{
                                        borderRadius: "6px",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        backgroundColor: "#000",
                                        color: "#fff",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                          backgroundColor: "#333",
                                        },
                                      }}
                                    >
                                      {btn.suggestion_text}
                                    </Button>
                                  ))}
                              </Stack>
                            </Box>
                          )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height={200}
                >
                  <CircularProgress size={30} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Template Preview Dialog (normal template + LLM variant) */}
      <Dialog
        open={whatsappPreviewOpen}
        onClose={handleCloseWhatsappPreview}
        maxWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            p: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0,0,0,0.5)",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 4,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            pt: 6,
            backgroundColor: "transparent",
          }}
        >
          {isWhatsappTemplatesLoading ? (
            <CircularProgress color="primary" />
          ) : (
            <Box
              sx={{
                width: "300px",
                minHeight: "400px",
                maxHeight: "75vh",
                backgroundColor: "#fff",
                border: "8px solid #000",
                borderRadius: "32px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 15px 50px rgba(0,0,0,0.3)",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "30px",
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#000",
                  color: "#fff",
                }}
              >
                <Typography sx={{ fontSize: "0.75rem" }}>
                  {dayjs().format("HH:mm")}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <WifiIcon sx={{ fontSize: "16px" }} />
                  <NetworkCellIcon sx={{ fontSize: "16px" }} />
                  <BatteryFullIcon sx={{ fontSize: "16px" }} />
                </Stack>
              </Box>

              <Button
                onClick={handleCloseWhatsappPreview}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  minWidth: 0,
                  padding: 0.5,
                  zIndex: 10,
                  color: "#000",
                }}
              >
                <ClearIcon fontSize="small" />
              </Button>

              <Box
                sx={{
                  p: 2,
                  overflowY: "auto",
                  flexGrow: 1,
                  backgroundColor: "#f5f5f5",
                  minHeight: "300px",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#ccc",
                    borderRadius: "2px",
                    "&:hover": {
                      bgcolor: "#999",
                    },
                  },
                }}
              >
                {previewTemplate ? (
                  (() => {
                    const template = previewTemplate;
                    const components = template.components || [];
                    const carouselComp = components.find(
                      (c) => c.type === "CAROUSEL",
                    );

                    if (carouselComp) {
                      const topBody = components.find((c) => c.type === "BODY");
                      return (
                        <Stack spacing={2}>
                          {topBody?.text && (
                            <Box
                              sx={{
                                backgroundColor: "#fff",
                                borderRadius: "8px",
                                p: 2,
                                borderBottom: "1px solid #ccc",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.9rem", color: "#333" }}
                              >
                                {topBody.text}
                              </Typography>
                            </Box>
                          )}

                          {carouselComp.cards?.map((card, cardIdx) => {
                            const cardHeader = card.components?.find(
                              (c) => c.type === "HEADER",
                            );
                            const cardBody = card.components?.find(
                              (c) => c.type === "BODY",
                            );
                            const cardBtns = card.components?.find(
                              (c) => c.type === "BUTTONS",
                            );

                            return (
                              <Box
                                key={cardIdx}
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: "8px",
                                  p: 2,
                                  borderBottom: "1px solid #ccc",
                                }}
                              >
                                {cardHeader?.format === "IMAGE" && (
                                  <HeaderImage
                                    url={
                                      cardHeader?.example?.header_handle?.[0] ||
                                      null
                                    }
                                    height={120}
                                  />
                                )}
                                {cardBody?.text && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: "#333",
                                      fontSize: "0.9rem",
                                      mb: 1,
                                    }}
                                  >
                                    {cardBody.text}
                                  </Typography>
                                )}
                                {cardBtns?.buttons?.length > 0 && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    gap={1}
                                  >
                                    {cardBtns.buttons.map((btn, bIdx) => (
                                      <Tooltip
                                        key={bIdx}
                                        title={btn.type === "PHONE_NUMBER" && btn.phone_number ? btn.phone_number : ""}
                                        arrow
                                        disableHoverListener={btn.type !== "PHONE_NUMBER" || !btn.phone_number}
                                      >
                                        <Button
                                          variant={bIdx === 0 ? "contained" : "outlined"}
                                          size="small"
                                          sx={{
                                            borderRadius: "6px",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                            ...(bIdx === 0
                                              ? {
                                                backgroundColor: "#000",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "#333" },
                                              }
                                              : {
                                                border: "1px solid #ccc",
                                                color: "#333",
                                                "&:hover": { backgroundColor: "#f0f0f0" },
                                              }),
                                          }}
                                        >
                                          {btn.text}
                                        </Button>
                                      </Tooltip>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      );
                    }

                    return (
                      <Stack spacing={2}>
                        {components.some((c) => c.type === "HEADER") && (
                          <Box
                            sx={{
                              backgroundColor: "#fff",
                              borderRadius: "8px",
                              p: 2,
                              borderBottom: "1px solid #ccc",
                            }}
                          >
                            {components
                              .filter((c) => c.type === "HEADER")
                              .map((component, idx) => (
                                <Box key={idx}>
                                  {component.format === "IMAGE" && (
                                    <HeaderImage
                                      url={
                                        component?.example
                                          ?.header_handle?.[0] || null
                                      }
                                      height={120}
                                    />
                                  )}
                                  {component.text && (
                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 700, color: "#000" }}
                                    >
                                      {component.text}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                          </Box>
                        )}

                        <Box
                          sx={{
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            p: 2,
                            borderBottom: "1px solid #ccc",
                          }}
                        >
                          {components
                            .filter((c) => c.type === "BODY")
                            .map((component, idx) => (
                              <Typography
                                key={idx}
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: "#333",
                                  lineHeight: 1.6,
                                  fontSize: "0.9rem",
                                  wordBreak: "break-word",
                                }}
                              >
                                {component.text}
                              </Typography>
                            ))}
                          {components
                            .filter((c) => c.type === "FOOTER")
                            .map((component, idx) => (
                              <Typography
                                key={idx}
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 1,
                                  color: "#999",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {component.text}
                              </Typography>
                            ))}
                        </Box>

                        {components.some((c) => c.type === "BUTTONS") && (
                          <Box
                            sx={{
                              backgroundColor: "#fff",
                              borderRadius: "8px",
                              p: 2,
                              borderBottom: "1px solid #ccc",
                            }}
                          >
                            <Stack spacing={1.5}>
                              {components
                                .filter((c) => c.type === "BUTTONS")
                                .map((component, idx) => (
                                  <Box key={idx}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        color: "#666",
                                        textTransform: "uppercase",
                                        fontSize: "0.65rem",
                                      }}
                                    >
                                      Suggested Actions
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                                    >
                                      {component.buttons.map((btn, bIdx) => (
                                        <Tooltip
                                          key={bIdx}
                                          title={btn.type === "PHONE_NUMBER" && btn.phone_number ? btn.phone_number : ""}
                                          arrow
                                          disableHoverListener={btn.type !== "PHONE_NUMBER" || !btn.phone_number}
                                        >
                                          <Button
                                            variant={bIdx === 0 ? "contained" : "outlined"}
                                            size="small"
                                            sx={{
                                              borderRadius: "6px",
                                              textTransform: "none",
                                              fontWeight: 600,
                                              fontSize: "0.75rem",
                                              ...(bIdx === 0
                                                ? {
                                                  backgroundColor: "#000",
                                                  color: "#fff",
                                                  "&:hover": { backgroundColor: "#333" },
                                                }
                                                : {
                                                  border: "1px solid #ccc",
                                                  color: "#333",
                                                  "&:hover": { backgroundColor: "#f0f0f0" },
                                                }),
                                            }}
                                          >
                                            {btn.text}
                                          </Button>
                                        </Tooltip>
                                      ))}
                                    </Stack>
                                  </Box>
                                ))}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    );
                  })()
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={200}
                  >
                    <Typography color="text.secondary">
                      Template data not found
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography sx={{ fontSize: "0.85rem" }}>{selectedText}</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CustomerSmsReport;